import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type {
  ArticleADeposer,
  Contrat,
  Deposant,
  GroupeCommission,
  SuggestionCategorie,
} from '@troc/shared'
import {
  FAMILLES,
  centimesDepuisSaisie,
  decomposerVente,
  eur,
  GROUPES_COMMISSION,
  LIBELLE_GROUPE_COMMISSION,
  RESUME_BAREME,
  tableauDemarque,
  tauxCommission,
  tauxDepuisSaisie,
  tauxEnPourcent,
} from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { Signature, signatureEnSvg } from '../composants/Signature'
import { CLIENT_VIDE, FormulaireClient } from '../composants/FormulaireClient'
import type { SaisieClient } from '../composants/FormulaireClient'
import { useToast } from '../etat/Toast'
import { imprimerContrat, imprimerEtiquettes } from '../impression/documents'

type Etape = 1 | 2 | 3

const ETAPES: { rang: Etape; nom: string }[] = [
  { rang: 1, nom: 'Déposant' },
  { rang: 2, nom: 'Articles & estimation' },
  { rang: 3, nom: 'Contrat & signature' },
]

interface Brouillon extends ArticleADeposer {
  cle: number
}

/** Ce que la boutique a l'habitude de demander pour une pièce comparable. */
interface SuggestionPrix {
  /** `ventes` : prix réellement encaissés. `rayon` : prix affichés. */
  origine: 'ventes' | 'rayon' | 'famille' | 'echelle'
  appuis: number
  medianeCentimes: number | null
  basCentimes: number | null
  hautCentimes: number | null
  /** Une poignée de prix ronds à toucher directement. */
  proposes: number[]
  /** Les ventes passées de pièces comparables, la plus récente en tête. */
  historique: {
    ref: string
    libelle: string
    date: string
    annee: number
    prixAfficheCentimes: number
    prixVenduCentimes: number
    /** Part du prix affiché abandonnée pour conclure, en pourcent. */
    remise: number
    /** Sur combien de mots de la désignation tapée l'article matche vraiment. */
    correspondance: 'forte' | 'partielle' | 'faible'
  }[]
}

let compteur = 0

/**
 * Des désignations d'exemple à jeux de mots, tirées au sort — un clin d'œil
 * dans un champ qui, sinon, n'affiche jamais que « Fauteuil scandinave teck ».
 * Ce n'est qu'un filigrane de saisie : rien n'est jamais enregistré tel quel.
 */
const PLACEHOLDERS_DESIGNATION = [
  'Fauteuil scandinave teck',
  'Lampe de chevet Aladin',
  'Vase qui a vu du pays',
  'Chaise longue de mémoire',
  'Théière pas si théière que ça',
  'Buffet froid, âme chaude',
  'Miroir, mon beau miroir',
  'Tabouret increvable (presque)',
  'Horloge qui a tout son temps',
  'Coffre à trésors (résultats variables)',
  'Chandelier plein de bonnes idées',
  'Panier à linge sale, propre',
  'Guéridon en fin de carrière',
  'Réveil qui ne réveille plus',
  'Bibliothèque IKEA increvable',
  'Commode qui ne se démonte pas',
  'Tapis qui a fait le tour du salon',
  'Canapé-lit, deux vies en un',
  'Service à thé pas très causant',
  'Malle qui garde ses secrets',
  'Porte-manteau au chômage technique',
  'Cadre sans photo, plein de vide',
  'Bureau qui a tout vu passer',
  'Étagère qui penche un peu, comme tout le monde',
  'Vaisselier plus vieux que le magasin',
  'Baladeur CD, souvenir garanti',
  'Aquarium sans les poissons',
  'Machine à écrire qui a tout dit',
  'Presse-agrumes, pressé de partir',
  'Cage à oiseaux, locataire envolé',
  'Radio qui capte encore un peu',
  'Valise qui n’ira plus très loin',
  'Cendrier d’avant l’interdiction',
  'Pendule qui retarde, comme d’habitude',
  'Vélo d’appartement, jamais essoufflé',
  'Fer à repasser, sans les plis',
  'Cafetière italienne, jamais pressée',
  'Bibelot dont personne ne connaît l’histoire',
  'Coussin qui a pris la forme du canapé',
  'Établi qui a fabriqué le voisin',
  'Statuette qui garde ses distances',
  'Skis qui n’ont plus la pêche',
  'Console de jeux, niveau poussière',
  'Robot ménager, la révolution est finie',
  'Guitare qui ne demande qu’à replaquer',
  'Poste de télé, l’écran d’avant l’écran plat',
  'Perceuse increvable, elle',
  'Nappe brodée par quelqu’un de patient',
  'Globe terrestre, un peu dépassé',
  'Sac à main qui a fait toutes les courses',
  'Chapeau qui a connu des jours meilleurs',
  'Écharpe tricotée avec amour, portée une fois',
  'Boîte à musique qui a une seule chanson',
  'Jumelles pour surveiller les voisins (discrètement)',
  'Cravate jamais nouée deux fois pareil',
  'Baromètre qui annonce toujours la pluie',
  'Casque de moto, plus de moto',
  'Théâtre de marionnettes sans acteurs',
  'Nain de jardin, gardien fidèle',
  'Sabots qui n’ont plus la cote',
  'Râteau qui n’a jamais raté personne',
  'Tondeuse à gazon, retraite bien méritée',
  'Poêle à bois qui a chauffé trois générations',
  'Landau d’un autre siècle',
  'Machine à coudre qui a habillé tout le quartier',
  'Presse à fleurs, souvenirs compris',
  'Longue-vue pour rêver plus loin',
  'Coffret à bijoux, la fortune en moins',
  'Balance de cuisine, toujours juste',
  'Trousse de couture increvable',
  'Chevalet qui attend son Picasso',
  'Encrier d’avant le stylo-bille',
  'Boussole qui indique surtout le nord du salon',
  'Diapositives, la vidéo d’avant la vidéo',
  'Vélo hollandais, le retour en grâce',
  'Étoffe qui a habillé plus d’un fauteuil',
  'Cave à vin sans les grands crus',
  'Poterie qui n’a pas pris une ride',
  'Roue de bicyclette, en solo',
  'Malle de voyage, sans le voyage',
  'Épuisette qui n’attrape que la poussière',
  'Meuble à chaussures, plus vide que prévu',
  'Théâtre de guignol, sans le méchant',
  'Cocotte-minute, elle n’est plus pressée',
  'Toupie qui a fini par s’arrêter',
  'Étrier sans le cheval',
  'Vieux poste à galène, ça grésille encore',
  'Coupe à fruits qui n’a jamais vu de fruit',
  'Métronome au tempo de brocante',
  'Herbier qui garde tout, même le temps',
  'Traîneau qui n’a plus vu de neige',
  'Écritoire pour lettres jamais envoyées',
  'Loupe pour lire entre les lignes',
  'Guéridon Napoléon III, la classe en moins cher',
  'Panier de pique-nique, sans le pique-nique',
  'Boîte à couture pleine de mystères',
  'Trompette qui a rendu son dernier souffle',
  'Écran de cinéma miniature, effet garanti',
  'Presse-purée increvable, lui aussi',
  'Coffret à cigares, sans les cigares',
]

function placeholderAleatoire(): string {
  return PLACEHOLDERS_DESIGNATION[Math.floor(Math.random() * PLACEHOLDERS_DESIGNATION.length)]!
}

export function Depot() {
  const { signaler, alerter } = useToast()
  const naviguer = useNavigate()

  // Arrivée depuis une fiche client : le déposant est déjà connu, on saute
  // l'étape de sélection pour ouvrir directement la saisie des articles.
  const emplacement = useLocation()
  const arrivee = emplacement.state as {
    deposant?: Deposant
    /** Nom et téléphone pris au rendez-vous, quand le client n'a pas de fiche. */
    nouveauClient?: { nom: string; gsm: string }
  } | null
  const deposantTransmis = arrivee?.deposant ?? null

  const [etape, setEtape] = useState<Etape>(1)
  const [deposant, setDeposant] = useState<Deposant | null>(null)
  const [enregistreGroupe, setEnregistreGroupe] = useState(false)
  /** Arrivée directe depuis une fiche client dont le solde n'est pas soldé :
   *  on pose la même question qu'à la recherche, avant d'ouvrir le dépôt. */
  const [arriveePaiement, setArriveePaiement] = useState<Deposant | null>(null)

  // L'onglet Dépôt reste monté en permanence (cf. `Volets` dans App.tsx), pour
  // retrouver un dépôt en cours d'un aller-retour à l'autre. Mais ça veut dire
  // que l'état initial ci-dessus ne se rejoue qu'au tout premier montage : un
  // « Nouveau dépôt » lancé depuis une fiche client, une fois l'onglet déjà
  // visité une première fois, arrivait avec le bon déposant en `state` sans
  // jamais sauter l'étape 1 — il fallait le rechercher une deuxième fois alors
  // qu'on l'avait déjà sous les yeux. `emplacement.key` change à chaque
  // navigation, y compris vers un chemin déjà monté : on s'en sert pour
  // détecter une vraie arrivée plutôt qu'un simple re-rendu.
  useEffect(() => {
    if (!deposantTransmis) return
    if ((deposantTransmis.soldeCentimes ?? 0) > 0) {
      // Même solde en attente que dans la recherche : même question, pas de
      // passe-droit parce qu'on vient de la fiche plutôt que de la liste.
      setArriveePaiement(deposantTransmis)
      return
    }
    setDeposant(deposantTransmis)
    setRappelPaiement(null)
    setEtape(2)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emplacement.key])

  /** Changer le groupe de commission depuis l'écran de dépôt, sans rouvrir la
   * fiche client complète — repéré nécessaire le 29/08/2026 (Trotin Johnny
   * resté au groupe par défaut alors qu'il est marchand). */
  async function changerGroupeCommission(groupe: GroupeCommission) {
    if (!deposant || groupe === deposant.groupeCommission) return
    setEnregistreGroupe(true)
    try {
      const { deposant: modifie } = await api<{ deposant: Deposant }>(
        `/deposants/${deposant.id}/groupe-commission`,
        { methode: 'PUT', corps: { groupe } },
      )
      setDeposant(modifie)
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Changement de groupe impossible.')
    } finally {
      setEnregistreGroupe(false)
    }
  }

  const [articles, setArticles] = useState<Brouillon[]>([])
  const [traces, setTraces] = useState<string[]>([])
  const [envoi, setEnvoi] = useState(false)
  // Ce qu'on imprime à la signature : les deux par défaut, comme avant —
  // mais un vendeur peut décocher l'un ou l'autre (étiquettes déjà réimprimées
  // la veille, contrat qu'on ne ressort pas pour un habitué…).
  const [imprimerContratChoisi, setImprimerContratChoisi] = useState(true)
  const [imprimerEtiquettesChoisi, setImprimerEtiquettesChoisi] = useState(true)
  /**
   * Un déposant qui avait de l'argent à reverser au moment où on l'a choisi,
   * et qui a préféré s'occuper du dépôt d'abord. On le lui repropose une fois
   * le contrat signé — le comptoir, c'est l'occasion, pas avant qu'il reparte.
   */
  const [rappelPaiement, setRappelPaiement] = useState<Deposant | null>(null)
  const [contratTermine, setContratTermine] = useState<Contrat | null>(null)

  const total = articles.reduce((somme, a) => somme + a.prixCentimes * a.quantite, 0)
  const commission = articles.reduce(
    (somme, a) =>
      somme +
      decomposerVente(a.prixCentimes, a.tauxCommission, deposant?.groupeCommission).commissionCentimes *
        a.quantite,
    0,
  )
  const nbEtiquettes = articles.reduce((somme, a) => somme + a.quantite, 0)

  const valider = async () => {
    if (!deposant || articles.length === 0 || envoi) return
    setEnvoi(true)
    try {
      const reponse = await api<{ contrat: Contrat }>('/depots', {
        methode: 'POST',
        corps: {
          deposantId: deposant.id,
          signatureSvg: signatureEnSvg(traces),
          // Seule la clé d'affichage reste à quai : tout le reste part, y
          // compris la quantité et la dévaluation choisies ligne par ligne.
          articles: articles.map(({ cle: _cle, ...article }) => article),
        },
      })
      if (imprimerContratChoisi) imprimerContrat(reponse.contrat)
      if (imprimerEtiquettesChoisi) imprimerEtiquettes(reponse.contrat.articles)
      const morceaux: string[] = []
      if (imprimerContratChoisi) morceaux.push('contrat')
      if (imprimerEtiquettesChoisi) {
        morceaux.push(`${reponse.contrat.articles.length} étiquette${reponse.contrat.articles.length > 1 ? 's' : ''}`)
      }
      signaler(
        `Contrat ${reponse.contrat.num} enregistré` +
          (morceaux.length ? ` · ${morceaux.join(' et ')} envoyé${morceaux.length > 1 ? 's' : ''} à l’imprimante.` : '.'),
      )
      // Le déposant avait un solde en attente au moment où on l'a choisi, et
      // avait préféré s'occuper d'abord du dépôt : on le lui repropose ici,
      // pendant qu'il est encore au comptoir, plutôt que de filer direct au
      // contrat.
      if (rappelPaiement) {
        setContratTermine(reponse.contrat)
      } else {
        naviguer(`/contrats/${reponse.contrat.num}`)
      }
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Ecran
      titre="Nouveau dépôt"
      sousTitre={
        deposant ? `${deposant.nom} · ${deposant.ref}` : 'Estimation, contrat, étiquettes'
      }
      outils={
        deposant && (
          <button
            type="button"
            className="btn btn-secondaire"
            style={{ marginLeft: 'auto' }}
            onClick={() => {
              setDeposant(null)
              setEtape(1)
            }}
          >
            Changer de client
          </button>
        )
      }
    >
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: etape === 1 ? '2px solid var(--color-divider)' : undefined,
          }}
        >
          <div className="etapes">
            {ETAPES.map((e) => (
              <button
                key={e.rang}
                type="button"
                className="etape"
                aria-current={etape === e.rang ? 'step' : undefined}
                disabled={(e.rang >= 2 && !deposant) || (e.rang === 3 && articles.length === 0)}
                onClick={() => setEtape(e.rang)}
              >
                <span className="rang">ÉTAPE {e.rang}</span>
                <span className="nom">{e.nom}</span>
              </button>
            ))}
          </div>

          {/* Le client, rappelé en clair pendant toute la saisie. Il n'était
              qu'en sous-titre gris : on saisit trente articles sans jamais
              revoir à qui ils appartiennent, et une erreur de client ne se
              découvre qu'à la signature. */}
          {deposant && etape !== 1 && (
            <div className="bandeau-deposant">
              <div className="bandeau-deposant-qui">
                <span className="etiquette">Dépôt pour</span>
                <span className="nom">
                  {deposant.titre ? `${deposant.titre} ` : ''}
                  {deposant.nom}
                </span>
              </div>
              <div className="bandeau-deposant-infos">
                <Link to={`/deposants/${deposant.id}`} className="ref">
                  {deposant.ref}
                </Link>
                {(deposant.gsm || deposant.tel) && <span>{deposant.gsm || deposant.tel}</span>}
                {deposant.ville && <span>{deposant.ville}</span>}
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Groupe
                  <select
                    value={deposant.groupeCommission}
                    disabled={enregistreGroupe}
                    title={LIBELLE_GROUPE_COMMISSION[deposant.groupeCommission]}
                    onChange={(e) => changerGroupeCommission(e.target.value as GroupeCommission)}
                  >
                    {GROUPES_COMMISSION.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {articles.length > 0 && (
                <span className="bandeau-deposant-compte">
                  {nbEtiquettes} pièce{nbEtiquettes > 1 ? 's' : ''} · {eur(total)}
                </span>
              )}
            </div>
          )}

          <div className="zone-defilante">
            {etape === 1 && (
              <ChoixDeposant
                selection={deposant}
                prerempli={arrivee?.nouveauClient ?? null}
                onChoisir={(d) => {
                  setDeposant(d)
                  setRappelPaiement(null)
                  setEtape(2)
                }}
                onPayerApres={(d) => {
                  setDeposant(d)
                  setRappelPaiement(d)
                  setEtape(2)
                }}
                onPayerMaintenant={(d) => naviguer(`/deposants/${d.id}`)}
                onErreur={alerter}
              />
            )}

            {etape === 2 && (
              <SaisieArticles
                articles={articles}
                tauxHabituel={deposant?.dernierTauxCommission ?? null}
                groupeCommission={deposant?.groupeCommission}
                onAjouter={(article) => {
                  compteur += 1
                  setArticles((liste) => [...liste, { ...article, cle: compteur }])
                }}
                onModifier={(cle, article) =>
                  setArticles((liste) =>
                    liste.map((a) => (a.cle === cle ? { ...article, cle } : a)),
                  )
                }
                onRetirer={(cle) => setArticles((liste) => liste.filter((a) => a.cle !== cle))}
                onErreur={alerter}
                onContinuer={() => setEtape(3)}
              />
            )}

            {etape === 3 && deposant && (
              <Recapitulatif
                deposant={deposant}
                articles={articles}
                total={total}
                commission={commission}
                nbEtiquettes={nbEtiquettes}
                traces={traces}
                setTraces={setTraces}
                envoi={envoi}
                imprimerContratChoisi={imprimerContratChoisi}
                onChangerImprimerContrat={setImprimerContratChoisi}
                imprimerEtiquettesChoisi={imprimerEtiquettesChoisi}
                onChangerImprimerEtiquettes={setImprimerEtiquettesChoisi}
                onValider={() => void valider()}
                onRetour={() => setEtape(2)}
              />
            )}
          </div>
        </div>

        {etape === 1 && <PanneauRegles />}
      </div>

      {contratTermine && rappelPaiement && (
        <RappelPaiement
          deposant={rappelPaiement}
          contrat={contratTermine}
          onPayerMaintenant={() => naviguer(`/deposants/${rappelPaiement.id}`)}
          onPlusTard={() => naviguer(`/contrats/${contratTermine.num}`)}
        />
      )}

      {arriveePaiement && (
        <ChoixPaiementArrivee
          deposant={arriveePaiement}
          onFermer={() => {
            setArriveePaiement(null)
            setDeposant(arriveePaiement)
            setRappelPaiement(null)
            setEtape(2)
          }}
          onApresCeDepot={() => {
            setArriveePaiement(null)
            setDeposant(arriveePaiement)
            setRappelPaiement(arriveePaiement)
            setEtape(2)
          }}
          onPayerMaintenant={() => {
            const d = arriveePaiement
            setArriveePaiement(null)
            naviguer(`/deposants/${d.id}`)
          }}
        />
      )}
    </Ecran>
  )
}

/**
 * Le contrat vient d'être signé — on repropose le paiement une dernière fois
 * avant de filer vers autre chose, pendant que le déposant est encore là.
 */
function RappelPaiement({
  deposant,
  contrat,
  onPayerMaintenant,
  onPlusTard,
}: {
  deposant: Deposant
  contrat: Contrat
  onPayerMaintenant: () => void
  onPlusTard: () => void
}) {
  return (
    <div className="voile voile-centre">
      <div className="panneau-cloture" role="dialog" aria-modal="true">
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>Contrat {contrat.num} enregistré</h4>
        </div>
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            {deposant.nom} a aussi <strong>{eur(deposant.soldeCentimes ?? 0)}</strong> à reverser,
            depuis avant ce dépôt. Il est encore au comptoir — c’est le bon moment.
          </div>
        </div>
        <div className="panneau-pied">
          <button type="button" className="btn btn-secondaire btn-large" onClick={onPlusTard}>
            Plus tard — voir le contrat
          </button>
          <button
            type="button"
            className="btn btn-primaire btn-large"
            style={{ flex: 1 }}
            onClick={onPayerMaintenant}
          >
            Payer maintenant — {eur(deposant.soldeCentimes ?? 0)}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Même question qu'à la recherche (`ChoixDeposant`), posée cette fois en
 * arrivant directement depuis la fiche client via « Nouveau dépôt » — le
 * solde en attente ne doit pas être traité différemment selon la porte
 * d'entrée.
 */
function ChoixPaiementArrivee({
  deposant,
  onFermer,
  onApresCeDepot,
  onPayerMaintenant,
}: {
  deposant: Deposant
  onFermer: () => void
  onApresCeDepot: () => void
  onPayerMaintenant: () => void
}) {
  return (
    <div className="voile voile-centre">
      <div className="panneau-cloture" role="dialog" aria-modal="true">
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>{deposant.nom} a {eur(deposant.soldeCentimes ?? 0)} à reverser</h4>
          <button
            type="button"
            className="btn btn-fantome"
            aria-label="Fermer — le déposant ne veut pas être payé maintenant"
            title="Le déposant ne veut pas être payé"
            style={{ fontSize: 20, lineHeight: 1, padding: '2px 8px' }}
            onClick={onFermer}
          >
            ×
          </button>
        </div>
        <div style={{ padding: '4px 20px 18px', color: 'var(--color-neutral-700)', fontSize: 13 }}>
          De ventes déjà passées, avant ce dépôt. Autant en profiter pendant qu’il est au comptoir —
          ou continuer le dépôt d’abord et y revenir juste après la signature.
        </div>
        <div className="panneau-pied">
          <button type="button" className="btn btn-secondaire btn-large" onClick={onApresCeDepot}>
            Après ce dépôt
          </button>
          <button
            type="button"
            className="btn btn-primaire btn-large"
            style={{ flex: 1 }}
            onClick={onPayerMaintenant}
          >
            Payer maintenant
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Étape 1 ─────────────────────────────────────────────────────────────────

function ChoixDeposant({
  selection,
  prerempli,
  onChoisir,
  onPayerApres,
  onPayerMaintenant,
  onErreur,
}: {
  selection: Deposant | null
  /** Venu d'un rendez-vous : on cherche déjà ce nom, et la création est amorcée. */
  prerempli: { nom: string; gsm: string } | null
  onChoisir: (deposant: Deposant) => void
  /** Il y a un solde en attente, mais le déposant préfère régler ça après le dépôt. */
  onPayerApres: (deposant: Deposant) => void
  onPayerMaintenant: (deposant: Deposant) => void
  onErreur: (message: string) => void
}) {
  const [requete, setRequete] = useState(prerempli?.nom ?? '')
  const [deposants, setDeposants] = useState<Deposant[]>([])
  const [creation, setCreation] = useState(false)
  /** Le déposant cliqué qui a un solde en attente — on demande d'abord quoi
   *  en faire, plutôt que de foncer sur l'étape 2 comme si de rien n'était. */
  const [aChoisirPaiement, setAChoisirPaiement] = useState<Deposant | null>(null)
  const [nouveau, setNouveau] = useState<SaisieClient>(
    prerempli ? { ...CLIENT_VIDE, nom: prerempli.nom, gsm: prerempli.gsm } : CLIENT_VIDE,
  )

  useEffect(() => {
    const controleur = new AbortController()
    const minuterie = window.setTimeout(() => {
      api<{ deposants: Deposant[] }>(
        `/deposants${requete ? `?q=${encodeURIComponent(requete)}` : ''}`,
        { signal: controleur.signal },
      )
        .then((r) => setDeposants(r.deposants))
        .catch(() => undefined)
    }, 220)
    return () => {
      window.clearTimeout(minuterie)
      controleur.abort()
    }
  }, [requete])

  const creer = async () => {
    if (!nouveau.nom.trim()) {
      onErreur('Le nom du client est requis.')
      return
    }
    try {
      const reponse = await api<{ deposant: Deposant }>('/deposants', {
        methode: 'POST',
        corps: nouveau,
      })
      onChoisir(reponse.deposant)
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Création impossible.')
    }
  }

  if (creation) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h4 style={{ margin: 0 }}>Nouveau client</h4>
        <FormulaireClient valeur={nouveau} onChange={setNouveau} />
        <div style={{ display: 'flex', gap: 10, maxWidth: 780 }}>
          <button
            type="button"
            className="btn btn-secondaire btn-large"
            onClick={() => setCreation(false)}
          >
            Retour
          </button>
          <button type="button" className="btn btn-primaire btn-large" onClick={() => void creer()}>
            Créer et continuer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
        <div className="champ" style={{ flex: 1 }}>
          <label htmlFor="recherche-deposant">Rechercher un client existant</label>
          <input
            id="recherche-deposant"
            className="saisie saisie-grande"
            placeholder="Nom, GSM, localité ou n° de client"
            value={requete}
            onChange={(e) => setRequete(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-secondaire btn-large"
          onClick={() => setCreation(true)}
        >
          Nouveau client
        </button>
      </div>

      <hr className="filet" style={{ margin: '2px 0' }} />

      {deposants.map((d) => (
        <button
          key={d.id}
          type="button"
          className="carte-choix"
          aria-pressed={selection?.id === d.id}
          onClick={() => ((d.soldeCentimes ?? 0) > 0 ? setAChoisirPaiement(d) : onChoisir(d))}
        >
          <span style={{ flex: 1 }}>
            <span className="titre" style={{ fontSize: 17, fontWeight: 800 }}>
              {d.nom}
            </span>
            <br />
            <span className="sourdine" style={{ fontSize: 12 }}>
              {d.ref} · {d.gsm || d.tel || 'sans téléphone'} · {d.ville || '—'} ·{' '}
              {d.articlesEnDepot ?? 0} pièce{(d.articlesEnDepot ?? 0) > 1 ? 's' : ''} en dépôt
            </span>
          </span>
          <span style={{ textAlign: 'right' }}>
            <span className="chiffre" style={{ fontSize: 20 }}>
              {eur(d.soldeCentimes ?? 0)}
            </span>
            <br />
            <span className="etiquette" style={{ fontSize: 10 }}>
              à reverser
            </span>
          </span>
        </button>
      ))}

      {deposants.length === 0 && <div className="vide">Aucun déposant ne correspond.</div>}

      {aChoisirPaiement && (
        <div className="voile voile-centre" onMouseDown={() => setAChoisirPaiement(null)}>
          <div
            className="panneau-cloture"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="panneau-entete">
              <h4 style={{ margin: 0 }}>
                {aChoisirPaiement.nom} a {eur(aChoisirPaiement.soldeCentimes ?? 0)} à reverser
              </h4>
              <button
                type="button"
                className="btn btn-fantome"
                aria-label="Fermer — le déposant ne veut pas être payé maintenant"
                title="Le déposant ne veut pas être payé"
                style={{ fontSize: 20, lineHeight: 1, padding: '2px 8px' }}
                onClick={() => {
                  const d = aChoisirPaiement
                  setAChoisirPaiement(null)
                  // Simple croix : on ferme le sujet, pas de rappel à la
                  // signature du contrat — contrairement à « Après ce dépôt ».
                  onChoisir(d)
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '4px 20px 18px', color: 'var(--color-neutral-700)', fontSize: 13 }}>
              De ventes déjà passées, avant ce dépôt. Autant en profiter pendant qu’il est au
              comptoir — ou continuer le dépôt d’abord et y revenir juste après la signature.
            </div>
            <div className="panneau-pied">
              <button
                type="button"
                className="btn btn-secondaire btn-large"
                onClick={() => {
                  const d = aChoisirPaiement
                  setAChoisirPaiement(null)
                  onPayerApres(d)
                }}
              >
                Après ce dépôt
              </button>
              <button
                type="button"
                className="btn btn-primaire btn-large"
                style={{ flex: 1 }}
                onClick={() => {
                  const d = aChoisirPaiement
                  setAChoisirPaiement(null)
                  onPayerMaintenant(d)
                }}
              >
                Payer maintenant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Étape 2 ─────────────────────────────────────────────────────────────────

function SaisieArticles({
  articles,
  tauxHabituel,
  groupeCommission,
  onAjouter,
  onModifier,
  onRetirer,
  onErreur,
  onContinuer,
}: {
  articles: Brouillon[]
  /** Dernier taux accordé à ce déposant — reproposé à chaque ligne. */
  tauxHabituel: number | null
  /** Le barème du déposant, pour l'aperçu de commission avant taux négocié. */
  groupeCommission?: GroupeCommission
  onAjouter: (article: ArticleADeposer) => void
  onModifier: (cle: number, article: ArticleADeposer) => void
  onRetirer: (cle: number) => void
  onErreur: (message: string) => void
  onContinuer: () => void
}) {
  /**
   * Le taux à afficher quand on commence une ligne. Toujours vide : le barème
   * du groupe du déposant s'applique par défaut, un taux négocié doit être
   * saisi explicitement à chaque fois plutôt que reproposé automatiquement —
   * sinon un ancien taux négocié (repris de l'ancien logiciel) écrase
   * silencieusement le nouveau barème par groupe (cf. 29/08/2026, Trotin
   * Johnny resté à 40 % au lieu du 45 % de son groupe B).
   */
  const tauxDeDepart = ''
  // Clé de la ligne en cours de correction. Une faute de frappe sur la
  // troisième pièce d'un dépôt de cinquante ne doit pas obliger à tout retaper.
  const [enCorrection, setEnCorrection] = useState<number | null>(null)
  const [titre, setTitre] = useState('')
  const [exemple, setExemple] = useState(placeholderAleatoire)
  const [categorie, setCategorie] = useState<string>(FAMILLES[0]!.libelle)
  // « Dévaluation progressive » : cochée par défaut, comme dans le logiciel
  // actuel. Décochée, la pièce garde son prix jusqu'au bout du contrat.
  const [devaluation, setDevaluation] = useState(true)
  const [prix, setPrix] = useState('')
  const [quantite, setQuantite] = useState('1')
  const [tauxNegocie, setTauxNegocie] = useState(tauxDeDepart)
  const [suggestion, setSuggestion] = useState<SuggestionCategorie | null>(null)
  const [prixSuggeres, setPrixSuggeres] = useState<SuggestionPrix | null>(null)
  // Dès que le vendeur choisit lui-même une catégorie, on cesse de la corriger.
  const [categorieForcee, setCategorieForcee] = useState(false)
  // Le clavier enchaîne les pièces sans la souris : Entrée sur la désignation
  // saute au prix, Entrée sur le prix (ou après) ajoute la ligne et revient ici.
  const refDesignation = useRef<HTMLInputElement>(null)
  const refPrix = useRef<HTMLInputElement>(null)

  // Suggestion de catégorie d'après la désignation, apprise de l'historique.
  useEffect(() => {
    if (titre.trim().length < 3) {
      setSuggestion(null)
      if (!categorieForcee) setCategorie(FAMILLES[0]!.libelle)
      return
    }
    const controleur = new AbortController()
    const minuterie = window.setTimeout(() => {
      api<{ suggestion: SuggestionCategorie | null }>(
        `/suggestion-categorie?titre=${encodeURIComponent(titre)}`,
        { signal: controleur.signal },
      )
        .then((r) => {
          setSuggestion(r.suggestion)
          // Sans suggestion, on ne laisse pas traîner la catégorie du mot
          // précédent : « livre » proposait Librairie, « jeu » ne propose rien
          // (trop ambigu) mais gardait Librairie affichée sans le dire.
          if (!categorieForcee) setCategorie(r.suggestion ? r.suggestion.categorie : FAMILLES[0]!.libelle)
        })
        .catch(() => undefined)
    }, 300)
    return () => {
      window.clearTimeout(minuterie)
      controleur.abort()
    }
  }, [titre, categorieForcee])

  // Prix pratiqués par la boutique sur des pièces comparables. La requête suit
  // la désignation *et* la famille : « lampe » ne vaut pas le même prix selon
  // qu'elle est classée en Luminaires ou en Collection.
  useEffect(() => {
    const controleur = new AbortController()
    const minuterie = window.setTimeout(() => {
      const p = new URLSearchParams({ titre: titre.trim(), categorie })
      api<SuggestionPrix>(`/suggestion-prix?${p}`, { signal: controleur.signal })
        .then(setPrixSuggeres)
        .catch(() => undefined)
    }, 300)
    return () => {
      window.clearTimeout(minuterie)
      controleur.abort()
    }
  }, [titre, categorie])

  const corriger = (article: Brouillon) => {
    setEnCorrection(article.cle)
    setTitre(article.titre)
    setCategorie(article.categorie)
    setCategorieForcee(true)
    setPrix((article.prixCentimes / 100).toFixed(2).replace('.', ',').replace(',00', ''))
    setQuantite(String(article.quantite))
    setTauxNegocie(
      article.tauxCommission === null || article.tauxCommission === undefined
        ? ''
        : String(Math.round(article.tauxCommission * 100)),
    )
    setDevaluation(article.demarqueActive !== false)
  }

  const renoncer = () => {
    setEnCorrection(null)
    setTitre('')
    setPrix('')
    setQuantite('1')
    setTauxNegocie(tauxDeDepart)
    setSuggestion(null)
    setCategorieForcee(false)
    setDevaluation(true)
    setExemple(placeholderAleatoire())
  }

  const centimes = centimesDepuisSaisie(prix)
  const taux = tauxDepuisSaisie(tauxNegocie)
  const apercu = centimes && centimes > 0 ? decomposerVente(centimes, taux, groupeCommission) : null

  const ajouter = () => {
    if (!titre.trim()) {
      onErreur('La désignation est obligatoire.')
      return
    }
    if (!centimes || centimes <= 0) {
      onErreur('Indiquez un prix de vente.')
      return
    }
    if (tauxNegocie.trim() !== '' && taux === null) {
      onErreur('Le taux négocié doit être un pourcentage entre 0 et 100.')
      return
    }
    const nombre = Number.parseInt(quantite, 10)
    if (!Number.isInteger(nombre) || nombre < 1 || nombre > 999) {
      onErreur('La quantité doit être comprise entre 1 et 999.')
      return
    }
    const saisi: ArticleADeposer = {
      titre: titre.trim(),
      categorie,
      prixCentimes: centimes,
      quantite: nombre,
      volumineux: false,
      tauxCommission: taux,
      demarqueActive: devaluation,
    }
    if (enCorrection !== null) onModifier(enCorrection, saisi)
    else onAjouter(saisi)
    setEnCorrection(null)
    setTitre('')
    setPrix('')
    setQuantite('1')
    setTauxNegocie(tauxDeDepart)
    setSuggestion(null)
    setCategorieForcee(false)
    setDevaluation(true)
    setExemple(placeholderAleatoire())
    // Retour à la désignation : le clavier peut enchaîner la pièce suivante
    // sans revenir à la souris.
    refDesignation.current?.focus()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          // Les cinq champs tiennent sur une seule ligne : la quantité et le
          // taux négocié n'ont plus de ligne à eux, qui restait à moitié vide
          // et poussait la liste des pièces déjà ajoutées vers le bas.
          gridTemplateColumns: '2fr 1.1fr 0.8fr 0.6fr 0.9fr',
          gap: '10px 12px',
          alignItems: 'end',
          maxWidth: 1040,
        }}
      >
        <div className="champ">
          <label htmlFor="designation">Désignation</label>
          <input
            id="designation"
            ref={refDesignation}
            className="saisie saisie-grande"
            placeholder={exemple}
            value={titre}
            autoFocus
            onChange={(e) => setTitre(e.target.value)}
            onKeyDown={(e) => {
              // Entrée saute au prix — pas de tentative d'ajout prématurée,
              // qui ne ferait qu'afficher « prix manquant » à chaque ligne.
              if (e.key === 'Enter') {
                e.preventDefault()
                refPrix.current?.focus()
                refPrix.current?.select()
              }
            }}
          />
        </div>
        <div className="champ">
          <label htmlFor="categorie">
            Catégorie
            {suggestion && !categorieForcee && (
              <span className="ref" style={{ marginLeft: 6 }}>
                · suggérée
              </span>
            )}
          </label>
          <select
            id="categorie"
            className="saisie saisie-grande"
            value={categorie}
            onChange={(e) => {
              setCategorie(e.target.value)
              setCategorieForcee(true)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                refPrix.current?.focus()
                refPrix.current?.select()
              }
            }}
          >
            {FAMILLES.map((f) => (
              <option key={f.code} value={f.libelle}>
                {f.code} · {f.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="prix">Prix de vente</label>
          <input
            id="prix"
            ref={refPrix}
            className="saisie saisie-grande"
            inputMode="decimal"
            placeholder="340"
            value={prix}
            onChange={(e) => setPrix(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                ajouter()
              }
            }}
          />
        </div>
        <div className="champ">
          <label htmlFor="quantite">Quantité</label>
          <input
            id="quantite"
            className="saisie saisie-grande"
            inputMode="numeric"
            value={quantite}
            // Le champ arrive pré-rempli à « 1 » : sans ça, taper « 5 » dessus
            // donne « 15 » au lieu de remplacer le 1 — repéré par Fabrice le
            // 31/08/2026.
            onFocus={(e) => e.target.select()}
            onChange={(e) => setQuantite(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                ajouter()
              }
            }}
          />
        </div>
        <div className="champ">
          <label htmlFor="taux">
            Taux négocié
            {/* Dire d'où vient le taux prérempli : sans cette mention, le
                vendeur croit à une erreur et le remet au barème. */}
            {tauxHabituel !== null && (
              <span className="sourdine"> · habituel chez ce déposant</span>
            )}
          </label>
          <input
            id="taux"
            className="saisie saisie-grande"
            inputMode="decimal"
            placeholder={`${Math.round(tauxCommission(centimes ?? 0, groupeCommission) * 100)} % par défaut`}
            value={tauxNegocie}
            onChange={(e) => setTauxNegocie(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                ajouter()
              }
            }}
          />
        </div>
        {prixSuggeres && prixSuggeres.proposes.length > 0 && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="etiquette" style={{ marginBottom: 6 }}>
              {prixSuggeres.origine === 'ventes'
                ? `D’après ${prixSuggeres.appuis} vente${prixSuggeres.appuis > 1 ? 's' : ''} comparable${prixSuggeres.appuis > 1 ? 's' : ''}`
                : prixSuggeres.origine === 'rayon'
                  ? `D’après ${prixSuggeres.appuis} article${prixSuggeres.appuis > 1 ? 's' : ''} en rayon`
                  : prixSuggeres.origine === 'famille'
                    ? `Aucun comparable — d’après vos ${prixSuggeres.appuis} articles en ${categorie}`
                    : 'Prix courants de la brocante'}
              {prixSuggeres.medianeCentimes !== null &&
                ` · habituellement ${eur(prixSuggeres.basCentimes!)} à ${eur(prixSuggeres.hautCentimes!)}`}
            </div>
            <div className="prix-proposes">
              {prixSuggeres.proposes.map((montant) => (
                <button
                  key={montant}
                  type="button"
                  className="palier"
                  aria-pressed={centimes === montant}
                  onClick={() => setPrix(String(Math.round(montant / 100)))}
                >
                  <span className="prix">{eur(montant)}</span>
                  {prixSuggeres.medianeCentimes === montant && (
                    <span className="pourcent">habituel</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <label className="case-a-cocher" style={{ gridColumn: '1 / 3' }}>
          <input
            type="checkbox"
            checked={devaluation}
            onChange={(e) => setDevaluation(e.target.checked)}
          />
          <span>
            Dévaluation progressive
            <span className="sourdine">
              {devaluation
                ? ' — le prix baissera mois après mois'
                : ' — décochée : le prix ne bougera pas'}
            </span>
          </span>
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          {enCorrection !== null && (
            <button type="button" className="btn btn-secondaire btn-large" onClick={renoncer}>
              Annuler
            </button>
          )}
          <button type="button" className="btn btn-primaire btn-large" onClick={ajouter}>
            {enCorrection !== null ? 'Enregistrer la correction' : '+ Ajouter au dépôt'}
          </button>
        </div>
      </div>

      <div className="encart-suggestion">
        {apercu
          ? `À ${eur(apercu.prixCentimes)}, la commission est de ${tauxEnPourcent(apercu.taux)}` +
            `${taux !== null ? ' (taux négocié)' : ''} — net déposant ${eur(apercu.netDeposantCentimes)}.`
          : RESUME_BAREME}
        {suggestion?.origine === 'historique' && suggestion.appuis > 0 && (
          <>
            {' '}
            Catégorie proposée d’après {suggestion.appuis} article
            {suggestion.appuis > 1 ? 's' : ''} déjà classé{suggestion.appuis > 1 ? 's' : ''}.
          </>
        )}
      </div>

      {articles.length > 0 && (
        <>
          <table className="tableau">
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Catégorie</th>
                <th className="a-droite">Qté</th>
                <th className="a-droite">Px. vente</th>
                <th className="a-droite">Commission</th>
                <th className="a-droite">PV. déposant</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => {
                const part = decomposerVente(article.prixCentimes, article.tauxCommission, groupeCommission)
                return (
                  <tr
                    key={article.cle}
                    aria-selected={enCorrection === article.cle}
                    className={enCorrection === article.cle ? 'ligne-corrigee' : undefined}
                  >
                    <td className="titre">{article.titre}</td>
                    <td className="sourdine">{article.categorie}</td>
                    <td className="a-droite chiffre">{article.quantite}</td>
                    <td className="a-droite chiffre">{eur(part.prixCentimes)}</td>
                    <td className="a-droite">
                      {tauxEnPourcent(part.taux)} ·{' '}
                      {eur(part.commissionCentimes * article.quantite)}
                    </td>
                    <td className="a-droite chiffre">
                      {eur(part.netDeposantCentimes * article.quantite)}
                    </td>
                    <td className="a-droite" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-fantome"
                        onClick={() => corriger(article)}
                        aria-label={`Modifier ${article.titre}`}
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondaire btn-icone"
                        onClick={() => {
                          if (enCorrection === article.cle) renoncer()
                          onRetirer(article.cle)
                        }}
                        aria-label={`Retirer ${article.titre}`}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div>
            <button type="button" className="btn btn-primaire btn-large" onClick={onContinuer}>
              Continuer vers le contrat →
            </button>
          </div>
        </>
      )}

      {prixSuggeres && prixSuggeres.historique.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <div className="etiquette" style={{ marginBottom: 6 }}>
            Ce qu’on a déjà vendu de comparable
          </div>
          {/* Une désignation ne matche jamais un mot à moitié : soit
              l'article partage tous les mots tapés (vrai comparable), soit
              seulement quelques-uns (piste), soit un seul (coïncidence,
              encore montré — mieux vaut une idée approximative que rien —
              mais jamais confondu avec un vrai comparable). */}
          <table className="tableau tableau-historique">
            <thead>
              <tr>
                <th>Article</th>
                <th>Ressemblance</th>
                <th className="a-droite">Année</th>
                <th className="a-droite">Affiché</th>
                <th className="a-droite">Vendu</th>
                <th className="a-droite">Écart</th>
              </tr>
            </thead>
            <tbody>
              {prixSuggeres.historique.map((v, i) => (
                <tr key={i} className={`ligne-correspondance-${v.correspondance}`}>
                  <td className="titre">
                    {v.libelle}
                    <br />
                    <span className="ref sourdine" style={{ fontSize: 10, fontWeight: 400 }}>
                      {v.ref}
                    </span>
                  </td>
                  <td>
                    <PastilleCorrespondance niveau={v.correspondance} />
                  </td>
                  <td className="a-droite">{v.annee}</td>
                  <td className="a-droite chiffre sourdine">{eur(v.prixAfficheCentimes)}</td>
                  <td className="a-droite chiffre">{eur(v.prixVenduCentimes)}</td>
                  <td className="a-droite">
                    {/* L'écart est l'information utile : un article
                        systématiquement bradé était mal estimé au départ. */}
                    {v.remise > 0 ? (
                      <span className="ecart-remise">−{v.remise} %</span>
                    ) : (
                      <span className="sourdine">plein tarif</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Étape 3 ─────────────────────────────────────────────────────────────────

function Recapitulatif({
  deposant,
  articles,
  total,
  commission,
  nbEtiquettes,
  traces,
  setTraces,
  envoi,
  imprimerContratChoisi,
  onChangerImprimerContrat,
  imprimerEtiquettesChoisi,
  onChangerImprimerEtiquettes,
  onValider,
  onRetour,
}: {
  deposant: Deposant
  articles: Brouillon[]
  total: number
  commission: number
  nbEtiquettes: number
  traces: string[]
  setTraces: (traces: string[]) => void
  envoi: boolean
  imprimerContratChoisi: boolean
  onChangerImprimerContrat: (choisi: boolean) => void
  imprimerEtiquettesChoisi: boolean
  onChangerImprimerEtiquettes: (choisi: boolean) => void
  onValider: () => void
  /** Revenir à la saisie des articles — Fabrice n'avait pas trouvé comment
   *  reculer depuis ce récapitulatif (31/08/2026) ; les onglets d'étape en
   *  haut d'écran le permettent déjà, mais ce bouton le rend explicite. */
  onRetour: () => void
}) {
  const demarque = useMemo(() => tableauDemarque(total), [total])
  const [demarqueOuverte, setDemarqueOuverte] = useState(false)
  const dernierPalier = demarque[demarque.length - 1]

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 380px', minWidth: 340, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="etiquette">Contrat de dépôt — 1 mois, puis démarque</div>
        <h3 style={{ margin: 0, fontSize: 24 }}>
          {deposant.nom} · {articles.length} pièce{articles.length > 1 ? 's' : ''}
        </h3>

        <table className="tableau">
          <thead>
            <tr>
              <th>Désignation</th>
              <th>Catégorie</th>
              <th className="a-droite">Qté</th>
              <th className="a-droite">Px. vente</th>
              <th className="a-droite">Commission</th>
              <th className="a-droite">Net déposant</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((article) => {
              const part = decomposerVente(
                article.prixCentimes,
                article.tauxCommission,
                deposant.groupeCommission,
              )
              return (
                <tr key={article.cle}>
                  <td className="titre">{article.titre}</td>
                  <td className="sourdine">{article.categorie}</td>
                  <td className="a-droite chiffre">{article.quantite}</td>
                  <td className="a-droite chiffre">{eur(part.prixCentimes)}</td>
                  <td className="a-droite sourdine">
                    {tauxEnPourcent(part.taux)} · {eur(part.commissionCentimes * article.quantite)}
                  </td>
                  <td className="a-droite chiffre">
                    {eur(part.netDeposantCentimes * article.quantite)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* La simulation mois par mois ne sert qu'à visualiser où mène la
            démarque — elle ne redit pas la liste des pièces. Repliée par
            défaut, elle ne dispute plus la place au vrai récapitulatif. */}
        {demarqueOuverte ? (
          <>
            <table className="tableau">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th className="a-droite">Prix affiché</th>
                  <th className="a-droite">Commission</th>
                  <th className="a-droite">Net déposant</th>
                </tr>
              </thead>
              <tbody>
                {demarque.map((ligne) => {
                  const part = decomposerVente(ligne.prixCentimes, null, deposant.groupeCommission)
                  return (
                    <tr key={ligne.mois}>
                      <td>{ligne.libelle}</td>
                      <td className="a-droite chiffre">{eur(ligne.prixCentimes)}</td>
                      <td className="a-droite sourdine">
                        {tauxEnPourcent(part.taux)} · {eur(part.commissionCentimes)}
                      </td>
                      <td className="a-droite chiffre">{eur(part.netDeposantCentimes)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <button
              type="button"
              className="btn btn-fantome"
              style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
              onClick={() => setDemarqueOuverte(false)}
            >
              Réduire la démarque
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-secondaire"
            style={{ justifyContent: 'space-between' }}
            onClick={() => setDemarqueOuverte(true)}
          >
            <span>
              Démarque prévue — de {eur(total)} à {dernierPalier ? eur(dernierPalier.prixCentimes) : ''}{' '}
              sur {demarque.length} mois
            </span>
            <span>+ Détail</span>
          </button>
        )}

        <div className="sourdine" style={{ fontSize: 12 }}>
          Récupérable sans frais à l’échéance du 1er mois. La démarque s’arrête à −80 % : au-delà,
          retrait ou solde. Reversement en espèces après le 15 du mois, par virement au-delà de
          3 000 €.
        </div>
      </div>

      <div style={{ flex: '0 1 340px', minWidth: 300, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="etiquette">Signature du déposant</div>
        <Signature traces={traces} onChange={setTraces} />
        <hr className="filet" style={{ margin: '4px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 14 }}>
          <span>Total déposé</span>
          <span className="chiffre" style={{ fontSize: 18 }}>{eur(total)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span>Commission estimée</span>
          <span>{eur(commission)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 14 }}>
          <span>Net pour {deposant.nom}</span>
          <span className="chiffre" style={{ fontSize: 18 }}>{eur(total - commission)}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="case-a-cocher">
            <input
              type="checkbox"
              checked={imprimerContratChoisi}
              onChange={(e) => onChangerImprimerContrat(e.target.checked)}
            />
            <span>Imprimer le contrat</span>
          </label>
          <label className="case-a-cocher">
            <input
              type="checkbox"
              checked={imprimerEtiquettesChoisi}
              onChange={(e) => onChangerImprimerEtiquettes(e.target.checked)}
            />
            <span>
              Imprimer {nbEtiquettes} étiquette{nbEtiquettes > 1 ? 's' : ''}
            </span>
          </label>
        </div>
        <button
          type="button"
          className="btn btn-primaire btn-large"
          disabled={envoi}
          onClick={onValider}
        >
          {envoi ? 'Enregistrement…' : 'Valider le dépôt'}
        </button>
        <button type="button" className="btn btn-secondaire" disabled={envoi} onClick={onRetour}>
          Retour aux articles
        </button>
      </div>
    </div>
  )
}

// ── Rappel des règles de dépôt ──────────────────────────────────────────────

function PanneauRegles() {
  return (
    <div className="panneau-rdv">
      <div className="etiquette">Règles de dépôt</div>
      <RegleDepot
        titre="Sur rendez-vous"
        detail="Du mardi au vendredi. Matin : 50 à 100 pièces. Après-midi : jusqu’à 12 pièces."
      />
      <RegleDepot
        titre="Sans rendez-vous"
        detail="2 pièces maximum, pour une valeur minimale de 50 € l’ensemble."
      />
      <RegleDepot
        titre="Pièces volumineuses"
        detail="Photos envoyées à l’avance pour acceptation et estimation. Place trouvée sous 10 jours."
      />
      <RegleDepot
        titre="État des articles"
        detail="Propres et en état de fonctionnement. L’achat direct est possible hors électronique."
      />
      <div
        className="sourdine"
        style={{
          marginTop: 'auto',
          fontSize: 11,
          borderTop: '2px solid var(--color-divider)',
          paddingTop: 10,
        }}
      >
        Le planning des rendez-vous n’est pas encore relié à l’application.
      </div>
    </div>
  )
}

const LIBELLE_CORRESPONDANCE = {
  forte: 'Comparable',
  partielle: 'Ressemblant',
  faible: 'Piste éloignée',
}

/** Le niveau de ressemblance, en un mot et une couleur — jamais présenté
 *  comme un vrai comparable quand ce n'en est pas un. */
function PastilleCorrespondance({ niveau }: { niveau: 'forte' | 'partielle' | 'faible' }) {
  return (
    <span className={`pastille-correspondance pastille-correspondance-${niveau}`}>
      {LIBELLE_CORRESPONDANCE[niveau]}
    </span>
  )
}

function RegleDepot({ titre, detail }: { titre: string; detail: string }) {
  return (
    <div style={{ borderBottom: '1px solid var(--color-divider)', paddingBottom: 10 }}>
      <div className="titre" style={{ fontSize: 14 }}>
        {titre}
      </div>
      <div className="sourdine" style={{ fontSize: 12 }}>
        {detail}
      </div>
    </div>
  )
}
